import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom';
import { subApi } from '../../config/api.config';
import useUserStore from '../../store/useUserStore';
import Loading from '../common/Loading';
import Pagination from '../common/Pagination';
import LimitSelect from '../common/LimitSelect';
import { formatAdminApiError } from '../../utils/formatAdminApiError';
import { SubscriptionPaymentNoteCell } from './SubscriptionPaymentNoteCell';

const SubscriptionsList = () => {
    const nav = useNavigate();
    const { userToken, isTokenValid } = useUserStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [subscriptions, setSubscriptions] = useState([]);
    const [allSubscriptions, setAllSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');

    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 10;

    useEffect(() => {
        if (!userToken || !isTokenValid()) {
            alert('관리자 권한이 없습니다.');
            nav('/login');
            return;
        }
    }, [userToken, isTokenValid, nav]);

    useEffect(() => {
        setLoading(true);
        subApi.get('/admin/subscriptions/active')
            .then(res => {
                console.log(res.data);
                let allSubscriptions = [];
                
                // API가 배열로 반환되는 경우
                if (Array.isArray(res.data)) {
                    allSubscriptions = res.data;
                } else {
                    // API가 객체로 반환되는 경우 (subscriptions, total 포함)
                    allSubscriptions = res.data.subscriptions || res.data || [];
                }
                
                setAllSubscriptions(allSubscriptions);
                setLoading(false);
            }).catch(err => {
                console.log(err);
                setLoading(false);
            })
    }, []);

    // 검색과 페이지네이션을 위한 별도 useEffect
    useEffect(() => {
        if (allSubscriptions.length > 0) {
            // 검색 필터링
            let filteredSubscriptions = allSubscriptions;
            if (searchTerm) {
                filteredSubscriptions = allSubscriptions.filter(subscription => {
                    // 차량번호 추출 (vehicleId 객체 또는 vehicleLicensePlate 스냅샷)
                    const vehicleId = subscription.vehicleId;
                    let licensePlate = '';
                    if (vehicleId && typeof vehicleId === 'object' && vehicleId.licensePlate) {
                        licensePlate = vehicleId.licensePlate;
                    } else if (subscription.vehicleLicensePlate) {
                        // vehicleId가 populate되지 않은 경우 스냅샷 사용
                        licensePlate = subscription.vehicleLicensePlate;
                    }
                    
                    const searchLower = searchTerm.toLowerCase();
                    return (
                        subscription.userId?.memberNickname?.toLowerCase().includes(searchLower) ||
                        subscription.userId?.memberEmail?.toLowerCase().includes(searchLower) ||
                        subscription.userId?.memberPhone?.includes(searchTerm) ||
                        licensePlate.toLowerCase().includes(searchLower)
                    );
                });
            }
            
            setTotal(filteredSubscriptions.length);
            
            // 클라이언트 사이드 페이지네이션
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);
            
            setSubscriptions(paginatedSubscriptions);
        }
    }, [allSubscriptions, page, limit, searchTerm]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage, limit });
    };

    const handleLimitChange = (newLimit) => {
        setSearchParams({ page: 1, limit: newLimit });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        // 검색 버튼이나 엔터를 눌렀을 때만 검색 실행
        setSearchTerm(searchInput);
        setSearchParams({ page: 1, limit });
    };

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleCancel = (subscription) => {
        // scheduledChanges에서 예약된 해지 확인
        const scheduledChanges = subscription.scheduledChanges || [];
        const hasCancellationScheduled = scheduledChanges.some(change => 
            change.kind === 'cancellation' && change.status === 'scheduled'
        );

        const isCancellationState = hasCancellationScheduled || subscription.status === 'cancellation_requested';

        if (isCancellationState) {
            if (window.confirm('해지 예약을 취소하시겠습니까?')) {
                subApi.post(`/admin/subscriptions/${subscription._id}/cancellation/revert`)
                    .then(() => {
                        setAllSubscriptions(allSubscriptions.map(sub => 
                            sub._id === subscription._id 
                                ? { 
                                    ...sub, 
                                    status: 'active',
                                    scheduledChanges: (sub.scheduledChanges || []).map(change => 
                                        change.kind === 'cancellation' && change.status === 'scheduled'
                                            ? { ...change, status: 'cancelled' }
                                            : change
                                    )
                                }
                                : sub
                        ));
                        alert('해지 예약이 취소되었습니다.');
                    })
                    .catch(err => {
                        console.log(err);
                        alert(`해지 예약 취소에 실패했습니다.\n${formatAdminApiError(err)}`);
                    });
            }
        } else {
            if(window.confirm('정말로 구독을 해지하시겠습니까?')) {
                subApi.post('/admin/subscriptions/unsubscribe', {
                    userId: subscription.userId._id,
                    subscriptionId: subscription._id
                })
                    .then(() => {
                        // 구독 상태를 cancellation_requested로 변경하고 scheduledChanges 추가(프론트 반영)
                        setAllSubscriptions(allSubscriptions.map(sub => 
                            sub._id === subscription._id 
                                ? { 
                                    ...sub, 
                                    status: 'cancellation_requested',
                                    scheduledChanges: [
                                        ...(sub.scheduledChanges || []).filter(change => change.kind !== 'cancellation'),
                                        {
                                            kind: 'cancellation',
                                            status: 'scheduled',
                                            effectiveDate: sub.nextPaymentDate,
                                            requestedAt: new Date(),
                                            payload: {}
                                        }
                                    ]
                                }
                                : sub
                        ));
                        alert('구독 해지 예약이 완료되었습니다.');
                    })
                    .catch(err => {
                        console.log(err);
                        alert(`구독 해지에 실패했습니다.\n${formatAdminApiError(err)}`);
                    });
            }
        }
    };

    const handleRefund = (subscription) => {
        if(window.confirm('정말로 환불하시겠습니까?')) {
            subApi.post('/admin/subscriptions/refund', {
                userId: subscription.userId._id,
                subscriptionId: subscription._id
            })
                .then(() => {
                    // 환불된 구독의 상태를 refunded로 변경
                    setAllSubscriptions(allSubscriptions.map(sub => 
                        sub._id === subscription._id 
                            ? { ...sub, status: 'refunded' }
                            : sub
                    ));
                    alert('환불이 완료되었습니다.');
                })
                .catch(err => {
                    console.log(err);
                    alert(`환불에 실패했습니다.\n${formatAdminApiError(err)}`);
                });
        }
    };


    if (!userToken || !isTokenValid()) {
        return null;
    }

    if (loading) {
        return <Loading text="구독 목록을 불러오는 중..." />;
    }

    const totalPages = Math.ceil(total / limit);
    const groupSize = 5;

    return (
        <div style={{ maxWidth: 1000, margin: '40px auto 0 auto', padding: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, borderBottom: '2px solid #1976d2', paddingBottom: 8, color: '#222', margin: 0 }}>
                    활성 구독 목록
                </h2>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1976d2', paddingBottom: 8 }}>
                    총 {total}명
                </div>
            </div>
            
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
                    <input
                        type="text"
                        placeholder="사용자명, 이메일, 전화번호, 차량번호로 검색"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearch(e);
                            }
                        }}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            width: '360px'
                        }}
                    />
                    <button
                        type="submit"
                        style={{
                            padding: '8px 16px',
                            background: '#1976d2',
                            color: '#fff',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'background 0.2s, color 0.2s'
                        }}
                        onMouseOver={e => {
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.color = '#1976d2';
                        }}
                        onMouseOut={e => {
                            e.currentTarget.style.background = '#1976d2';
                            e.currentTarget.style.color = '#fff';
                        }}
                    >
                        검색
                    </button>
                </form>
                <LimitSelect limit={limit} onLimitChange={handleLimitChange} />
            </div>
            <div style={{ minHeight: 220 }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '5%' }}>No.</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '16%' }}>사용자</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '11%' }}>차량번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '10%' }}>상품</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '11%' }}>전화번호</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '9%' }}>세차횟수</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '14%' }}>만료일</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '16%' }}>정기결제 메모</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '9%' }}>상태</th>
                                    <th style={{ borderBottom: '2px solid #1976d2', padding: '14px 8px', textAlign: 'center', fontWeight: 700, color: '#222', width: '18%' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptions.length > 0 ? (
                                    subscriptions.map((subscription, index) => {
                                        const rowNumber = (page - 1) * limit + index + 1;
                                        return (
                                        <tr key={subscription._id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#888', textAlign: 'center', fontWeight: '500' }}>
                                                {rowNumber}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                <div style={{ marginBottom: '4px', fontWeight: '600' }}>
                                                    {subscription.userId?.memberNickname || '-'}
                                                </div>
                                                <div style={{ fontSize: 14, color: '#666' }}>
                                                    {subscription.userId?.memberEmail || '-'}
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {(() => {
                                                    // 1. vehicleId가 populate된 경우
                                                    const v = subscription.vehicleId;
                                                    if (v && typeof v === 'object' && v.licensePlate) {
                                                        return v.licensePlate;
                                                    }
                                                    // 2. vehicleLicensePlate 스냅샷 사용
                                                    if (subscription.vehicleLicensePlate) {
                                                        return subscription.vehicleLicensePlate;
                                                    }
                                                    // 3. 차량번호를 찾을 수 없는 경우
                                                    return '-';
                                                })()}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {subscription.productId === 'daily_subscription' ? '데일리' : 
                                                 subscription.productId === 'all_in_one_subscription' ? '올인원' :
                                                 subscription.productId}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {subscription.userId?.memberPhone || '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {subscription.washCount || '-'}회
                                            </td>
                                            <td style={{ padding: '14px 10px', fontSize: 16, color: '#444', textAlign: 'center' }}>
                                                {subscription.expirationDate ? formatDate(subscription.expirationDate) : '-'}
                                            </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center', verticalAlign: 'top' }}>
                                                <SubscriptionPaymentNoteCell subscription={subscription} />
                                            </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                                {(() => {
                                                    // scheduledChanges에서 해지 예약 확인
                                                    const scheduledChanges = subscription.scheduledChanges || [];
                                                    const hasCancellationScheduled = scheduledChanges.some(change => 
                                                        change.kind === 'cancellation' && change.status === 'scheduled'
                                                    );
                                                    
                                                    let displayStatus = subscription.status;
                                                    let backgroundColor = '#ffe8e8';
                                                    let color = '#d32f2f';
                                                    
                                                    if (subscription.status === 'active') {
                                                        if (hasCancellationScheduled) {
                                                            displayStatus = '해지예약';
                                                            backgroundColor = '#fff3e0';
                                                            color = '#f57c00';
                                                        } else {
                                                            displayStatus = '활성';
                                                            backgroundColor = '#e8f5e8';
                                                            color = '#2e7d32';
                                                        }
                                                    } else if (subscription.status === 'cancellation_requested') {
                                                        displayStatus = '해지예약';
                                                        backgroundColor = '#fff3e0';
                                                        color = '#f57c00';
                                                    } else if (subscription.status === 'cancelled') {
                                                        displayStatus = '취소';
                                                        backgroundColor = '#ffe8e8';
                                                        color = '#d32f2f';
                                                    } else if (subscription.status === 'refunded') {
                                                        displayStatus = '환불';
                                                        backgroundColor = '#f3e5f5';
                                                        color = '#7b1fa2';
                                                    }
                                                    
                                                    return (
                                                        <span style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: 12,
                                                            fontWeight: 500,
                                                            backgroundColor,
                                                            color
                                                        }}>
                                                            {displayStatus}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ padding: '14px 10px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                    <button
                                                        style={{
                                                            padding: '7px 16px',
                                                            background: (() => {
                                                                const scheduledChanges = subscription.scheduledChanges || [];
                                                                const hasCancellationScheduled = scheduledChanges.some(change => 
                                                                    change.kind === 'cancellation' && change.status === 'scheduled'
                                                                );
                                                                return (hasCancellationScheduled || subscription.status === 'cancellation_requested') ? '#f57c00' : '#1976d2';
                                                            })(),
                                                            color: '#fff',
                                                            border: (() => {
                                                                const scheduledChanges = subscription.scheduledChanges || [];
                                                                const hasCancellationScheduled = scheduledChanges.some(change => 
                                                                    change.kind === 'cancellation' && change.status === 'scheduled'
                                                                );
                                                                return (hasCancellationScheduled || subscription.status === 'cancellation_requested') ? '1px solid #f57c00' : '1px solid #1976d2';
                                                            })(),
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 15,
                                                            transition: 'background 0.2s, color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'center',
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            const scheduledChanges = subscription.scheduledChanges || [];
                                                            const hasCancellationScheduled = scheduledChanges.some(change => 
                                                                change.kind === 'cancellation' && change.status === 'scheduled'
                                                            );
                                                            const isCancellationState = hasCancellationScheduled || subscription.status === 'cancellation_requested';
                                                            e.currentTarget.style.color = isCancellationState ? '#f57c00' : '#1976d2';
                                                            e.currentTarget.style.border = isCancellationState ? '1px solid #f57c00' : '1px solid #1976d2';
                                                        }}
                                                        onMouseOut={e => {
                                                            const scheduledChanges = subscription.scheduledChanges || [];
                                                            const hasCancellationScheduled = scheduledChanges.some(change => 
                                                                change.kind === 'cancellation' && change.status === 'scheduled'
                                                            );
                                                            const isCancellationState = hasCancellationScheduled || subscription.status === 'cancellation_requested';
                                                            e.currentTarget.style.background = isCancellationState ? '#f57c00' : '#1976d2';
                                                            e.currentTarget.style.color = '#fff';
                                                            e.currentTarget.style.border = isCancellationState ? '1px solid #f57c00' : '1px solid #1976d2';
                                                        }}
                                                        onClick={() => handleCancel(subscription)}
                                                    >{(() => {
                                                        const scheduledChanges = subscription.scheduledChanges || [];
                                                        const hasCancellationScheduled = scheduledChanges.some(change => 
                                                            change.kind === 'cancellation' && change.status === 'scheduled'
                                                        );
                                                        const isCancellationState = hasCancellationScheduled || subscription.status === 'cancellation_requested';
                                                        return isCancellationState ? '해지 취소' : '해지';
                                                    })()}</button>
                                                    <button
                                                        style={{
                                                            padding: '7px 16px',
                                                            background: '#d32f2f',
                                                            color: '#fff',
                                                            border: '1px solid #d32f2f',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: 15,
                                                            transition: 'background 0.2s, color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'center',
                                                        }}
                                                        onMouseOver={e => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.color = '#d32f2f';
                                                            e.currentTarget.style.border = '1px solid #d32f2f';
                                                        }}
                                                        onMouseOut={e => {
                                                            e.currentTarget.style.background = '#d32f2f';
                                                            e.currentTarget.style.color = '#fff';
                                                            e.currentTarget.style.border = '1px solid #d32f2f';
                                                        }}
                                                        onClick={() => handleRefund(subscription)}
                                                    >환불</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="10" style={{ 
                                            padding: '40px 20px', 
                                            textAlign: 'center', 
                                            color: '#666',
                                            fontSize: '14px'
                                        }}>
                                            활성 구독이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        
                        <Pagination 
                            page={page} 
                            totalPages={totalPages} 
                            groupSize={groupSize} 
                            onChange={handlePageChange} 
                        />
                    </>
                )}
            </div>
        </div>
    )
}

export default SubscriptionsList